import { INTERACTIONS_TABLE_PREFIX } from "constants/interactions";
import { useState, useMemo, useCallback, useEffect } from "react";
import { usePrevious } from "react-use";
import {
    IAgentChartDragOptions,
    IMetricChartFilterStartSelection,
    IMouseEventsCallback,
} from "./use-agent-chart-selection.types";
import { pascalize, camelize } from "humps";
import { useRemoveUrlParameters } from "hooks-for/urls/use-remove-url-parameters";
import { useToggleUrlParameters } from "hooks-for/urls/use-toggle-url-parameters";
import { IMetricChartFilterSelection } from "types-for/charts/metric-chart.types";
import { getMaximumValue, getMinimumValue } from "./use-agent-chart-selection.utilities";
import { isBooleanMetric } from "utilities-for/metrics/is-boolean-metric";

/**
 * Custom hook to add interaction (click and drag selection) to a chart using values
 * @param props.beforeUpdateUrl Callback for any changes wished to be made before updating the URL
 * @param props.endValue
 * @param props.startValue
 * @param props.metric      Current metric selected
 */
export const useAgentChartSelection = ({ startValue, endValue, metric, beforeUpdateUrl }: IAgentChartDragOptions) => {
    const [isMouseDown, setIsMouseDown] = useState<boolean>(false);
    const [mouseStartPosition, setMouseStartPosition] = useState<number | null>(null);

    // Start selection with active minimum and maximum values
    const [selectedStartPosition, setSelectedStartPosition] = useState<IMetricChartFilterStartSelection>({
        activeLabel: null,
        activeValues: [-1, -1],
    });

    const [filterSelection, setFilterSelection] = useState<[IMetricChartFilterSelection, IMetricChartFilterSelection]>([
        { activeLabel: null, activeValue: startValue },
        { activeLabel: null, activeValue: endValue },
    ]);

    const removeUrlParameters = useRemoveUrlParameters();
    const toggleUrlParameters = useToggleUrlParameters();

    // Previous filter selected
    const previousFilterRange = usePrevious(filterSelection);

    /**
     * Update chart area
     * @param chartFilterArea The chart filter area for one side of the filter range.
     * @param side            The side of the filter range being set.
     */
    const setChartFilterArea = (chartFilterArea: any, side: "left" | "right") => {
        if (side === "left") setFilterSelection(([, right]) => [chartFilterArea, right]);
        else setFilterSelection(([left]) => [left, chartFilterArea]);
    };

    /**
     * Update only the filter range
     * @param updateStartValue
     * @param updateEndValue
     */
    const updateFilterRange = (updateStartValue: number, updateEndValue: number) => {
        setFilterSelection(([left, right]) => [
            { ...left, activeValue: updateStartValue },
            { ...right, activeValue: updateEndValue },
        ]);
    };

    /**
     * Reset the selected date and remove it from url
     */
    const resetUrlDateSelection = useCallback(() => {
        const metricTitle = pascalize(metric);
        if (isBooleanMetric(metric)) {
            removeUrlParameters([`${INTERACTIONS_TABLE_PREFIX}.${camelize(metricTitle)}`]);
            return;
        }
        removeUrlParameters([
            `${INTERACTIONS_TABLE_PREFIX}.minimum${metricTitle}`,
            `${INTERACTIONS_TABLE_PREFIX}.maximum${metricTitle}`,
        ]);
    }, [metric, removeUrlParameters]);

    /**
     * Set the selected area as query parameters
     * @param selectedArea Referrence date area
     */
    const updateUrlValueSelection = useCallback(
        (selectedArea: [number, number]) => {
            const [minimumSelectedValue, maximumSelectedValue] = selectedArea;
            const metricTitle = pascalize(metric);

            if (isBooleanMetric(metric)) {
                // Only set the parameters if they're both the same e.g true and true. We want to reset the URL if
                // they're different because that's the same as not having selected at all.
                if (minimumSelectedValue === maximumSelectedValue) {
                    toggleUrlParameters({
                        [`${INTERACTIONS_TABLE_PREFIX}.${camelize(metricTitle)}`]: minimumSelectedValue,
                    });
                    return;
                }
                resetUrlDateSelection();
                return;
            }
            toggleUrlParameters({
                [`${INTERACTIONS_TABLE_PREFIX}.minimum${metricTitle}`]: minimumSelectedValue,
                [`${INTERACTIONS_TABLE_PREFIX}.maximum${metricTitle}`]: maximumSelectedValue,
            });
        },
        [metric, resetUrlDateSelection, toggleUrlParameters]
    );

    /**
     * Reset the chart so that no selection area is shown and reset the dates so that the interactions below the chart
     * are no longer filtered.
     */
    const resetChartFilter = useCallback(() => {
        setFilterSelection([
            { activeLabel: null, activeValue: startValue },
            { activeLabel: null, activeValue: endValue },
        ]);

        resetUrlDateSelection();
    }, [startValue, endValue, resetUrlDateSelection]);

    /**
     * A function that is called once the the user has finished filtering the chart. It makes sure the dates are in
     * the right order and then updates the dates used for filtering the interactions displayed below the chart.
     */
    const onFilterComplete = useCallback(() => {
        // Apply any changes to filter selection before updating the URL with their values
        const [left, right] =
            typeof beforeUpdateUrl === "function"
                ? beforeUpdateUrl([filterSelection[0], filterSelection[1]])
                : filterSelection;

        const [startValue, endValue] =
            left.activeValue < right.activeValue
                ? [left.activeValue, right.activeValue]
                : [right.activeValue, left.activeValue];

        updateUrlValueSelection([startValue, endValue]);
    }, [filterSelection, beforeUpdateUrl, updateUrlValueSelection]);

    // Mouse event callbacks
    const mouseEventCallbacks: IMouseEventsCallback = useMemo(() => {
        return {
            onMouseDown: (event: any) => {
                setIsMouseDown(true);
                setMouseStartPosition(event?.activeCoordinate?.x);
                const payloadMiniumValue = getMinimumValue(metric, event);
                const payloadMaximumValue = getMaximumValue(metric, event);
                setSelectedStartPosition({
                    activeLabel: event?.activeLabel ?? null,
                    activeValues: [payloadMiniumValue, payloadMaximumValue],
                });

                if (payloadMiniumValue !== null && payloadMaximumValue !== null) {
                    setFilterSelection([
                        { activeLabel: event?.activeLabel ?? null, activeValue: payloadMiniumValue },
                        // for a single selection (click) both sides will have the same activeLabel
                        { activeLabel: event?.activeLabel ?? null, activeValue: payloadMaximumValue },
                    ]);
                }
            },
            onMouseMove: (event: any) => {
                if (isMouseDown) {
                    const mouseMovePosition = event?.activeCoordinate?.x;
                    const payloadMiniumValue = getMinimumValue(metric, event);
                    const payloadMaximumValue = getMaximumValue(metric, event);

                    if (payloadMiniumValue !== null && payloadMaximumValue !== null) {
                        // Using the mouse position relative to initial mouse click
                        // update either left or right side
                        if (mouseMovePosition < mouseStartPosition!) {
                            // Moving on the left, set any new selection to the left and to the right
                            // will always be the point when onClick happened first
                            setFilterSelection([
                                { activeLabel: event?.activeLabel ?? null, activeValue: payloadMiniumValue },

                                {
                                    activeLabel: selectedStartPosition.activeLabel,
                                    activeValue: selectedStartPosition.activeValues[1],
                                },
                            ]);
                        } else {
                            // Moving on the right, set any new selection to the right and to the left
                            // will always be the point when onClick happened first
                            setFilterSelection([
                                {
                                    activeLabel: selectedStartPosition.activeLabel,
                                    activeValue: selectedStartPosition.activeValues[0],
                                },
                                { activeLabel: event?.activeLabel ?? null, activeValue: payloadMaximumValue },
                            ]);
                        }
                    }
                }
            },
            onMouseUp: () => {
                setIsMouseDown(false);
                setMouseStartPosition(null);
                setSelectedStartPosition({ activeLabel: null, activeValues: [-1, -1] });

                // Call the filter complete callback if both active labels are defined. Otherwise, reset.
                if (
                    filterSelection.every(
                        ({ activeLabel }) => typeof activeLabel === "number" || typeof activeLabel === "string"
                    )
                ) {
                    // Filter range has the same active label for all entries
                    if (filterSelection.every(({ activeLabel }) => activeLabel === filterSelection[0].activeLabel)) {
                        // Filter range is the same as previous
                        if (
                            filterSelection.every(
                                ({ activeLabel }, index) => previousFilterRange![index].activeLabel === activeLabel
                            )
                        ) {
                            resetChartFilter();
                        } else {
                            onFilterComplete();
                        }
                    } else {
                        onFilterComplete();
                    }
                } else {
                    resetChartFilter();
                }
            },
        };
    }, [
        filterSelection,
        isMouseDown,
        metric,
        mouseStartPosition,
        onFilterComplete,
        previousFilterRange,
        resetChartFilter,
        selectedStartPosition.activeLabel,
        selectedStartPosition.activeValues,
    ]);

    // Initialize default values
    useEffect(() => {
        setFilterSelection([
            { activeLabel: null, activeValue: startValue },
            { activeLabel: null, activeValue: endValue },
        ]);
    }, [endValue, startValue]);

    return {
        filterSelection,
        onFilterComplete,
        mouseEventCallbacks,
        setChartFilterArea,
        resetChartFilter,
        updateFilterRange,
    };
};
